import { Plus, Trash2, Lock, AlertTriangle, Loader2 } from 'lucide-react'
import { useCostCenters } from '../hooks/useAccounting'

export interface CostCenterAllocation {
  cost_center_id: string
  percentage: string
}

interface Props {
  allocations: CostCenterAllocation[]
  onChange: (allocations: CostCenterAllocation[]) => void
}

export function CostCenterAllocator({ allocations, onChange }: Props) {
  const { data, isLoading, isError, isForbidden, refetch } = useCostCenters({ is_active: 'true' })
  const costCenters = data?.cost_centers ?? []

  const totalPct = allocations.reduce(
    (sum, a) => sum + (parseFloat(a.percentage) || 0),
    0,
  )
  const isValid = allocations.length === 0 || Math.abs(totalPct - 100) < 0.01

  const addRow = () => {
    onChange([...allocations, { cost_center_id: '', percentage: '' }])
  }

  const removeRow = (index: number) => {
    onChange(allocations.filter((_, i) => i !== index))
  }

  const updateRow = (index: number, field: keyof CostCenterAllocation, value: string) => {
    const updated = allocations.map((a, i) =>
      i === index ? { ...a, [field]: value } : a,
    )
    onChange(updated)
  }

  // IDs ya seleccionados (para evitar duplicados en dropdowns)
  const selectedIds = new Set(allocations.map(a => a.cost_center_id))

  // Los cuatro estados tienen que ser distinguibles. Antes se hacía
  // `data?.cost_centers ?? []` sin mirar el error, así que un 403 —el caso real
  // en producción: este componente vive en ventas, compras y nómina, pero el
  // endpoint exige el módulo de contabilidad— se veía IDÉNTICO a "esta empresa
  // no tiene centros de costo configurados".
  if (isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-content-subtle">
        <Loader2 size={13} className="animate-spin" aria-hidden="true" />
        Cargando centros de costo…
      </p>
    )
  }

  if (isForbidden) {
    return (
      <div className="flex items-start gap-1.5 text-xs text-content-muted" role="status">
        <Lock size={13} className="mt-0.5 shrink-0 text-content-subtle" aria-hidden="true" />
        <span>
          No tienes permiso para ver los centros de costo, así que no puedes imputarlos en este
          documento. Si necesitas hacerlo, pide el permiso de <strong>Centros de Costo</strong> a
          un administrador.
        </span>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-start gap-1.5 text-xs text-danger" role="alert">
        <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          No se pudieron cargar los centros de costo.{' '}
          <button type="button" onClick={() => refetch()} className="underline hover:no-underline">
            Reintentar
          </button>
        </span>
      </div>
    )
  }

  // Cargó bien pero la empresa no tiene ninguno: eso NO es un error, y hay que
  // decirlo distinto de los dos casos de arriba.
  if (costCenters.length === 0 && allocations.length === 0) {
    return (
      <p className="text-xs text-content-subtle">
        No hay centros de costo configurados. Se crean en Contabilidad → Centros de Costo.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {allocations.map((alloc, i) => (
        <div key={i} className="flex items-center gap-2">
          <select aria-label="Centro de Costo"
            value={alloc.cost_center_id}
            onChange={e => updateRow(i, 'cost_center_id', e.target.value)}
            className="flex-1 border border-divider-strong rounded px-2 py-1 text-sm"
          >
            <option value="">— Centro de Costo —</option>
            {costCenters
              .filter(cc => cc.id === alloc.cost_center_id || !selectedIds.has(cc.id))
              .map(cc => (
                <option key={cc.id} value={cc.id}>
                  {cc.code} — {cc.name}
                </option>
              ))}
          </select>
          <div className="relative">
            <input aria-label="Porcentaje asignado"
              type="number"
              min="0"
              max="100"
              step="1"
              value={alloc.percentage}
              onChange={e => updateRow(i, 'percentage', e.target.value)}
              placeholder="0"
              className="w-20 border border-divider-strong rounded px-2 py-1 text-sm text-right pr-6"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-content-subtle">%</span>
          </div>
          <button
            type="button"
            onClick={() => removeRow(i)}
            className="text-danger-solid hover:text-danger-content p-1"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={addRow}
          className="text-xs text-primary hover:text-primary-hover flex items-center gap-1"
        >
          <Plus size={12} /> Agregar CC
        </button>
        {allocations.length > 0 && (
          <span
            className={`text-xs font-medium ${
              isValid ? 'text-success-content-soft' : 'text-danger-content-soft'
            }`}
          >
            Total: {totalPct.toFixed(2)}%
          </span>
        )}
      </div>
    </div>
  )
}
